import React, { useContext, useEffect, useMemo, useState } from 'react';
import Grid from '@mui/material/Grid';
import { Box, IconButton, Tooltip, InputAdornment } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';
import FormHelperText from '@mui/material/FormHelperText';
import {
  readTerminologyAvailability,
  terminologyAvailabilityFromError
} from 'src/util/terminologyAvailability';
import { AppContext, SnackContext } from 'src/store/ContextStore';
import FormLabel from 'src/components/UI/input/CustomFormLabel';
import TextInput from 'src/components/UI/input/TextInput';
import DateInput from 'src/components/UI/input/DateInput';
import ModalButtons from 'src/components/UI/button/ModalButtons';
import ClinicalNoteDeleteModal from '../../diagnoses-menu/ClinicalNoteDeleteModal';
import { useClinicalSuggestions } from 'src/hooks/useClinicalSuggestions';
import ClinicalSuggestionsPanel from 'src/components/UI/ClinicalSuggestionsPanel';
import { calculateAgeOnDate } from 'src/util/util';
import { toLocalDateFromPickerOrImport } from 'src/util/partialDate';

const TREATMENT_SEARCH_MIN_QUERY_LENGTH = 2;
const TREATMENT_SEARCH_DEBOUNCE_MS = 300;

const getOptionLabel = (option) => {
  if (!option) return '';
  if (typeof option === 'string') return option;
  return option.label || option.value || '';
};

const mergeOptions = (options = [], selectedValue = '') => {
  const merged = [];
  const seen = new Set();
  const add = (option) => {
    const label = getOptionLabel(option);
    if (!label) return;
    const key = label.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    merged.push(typeof option === 'string' ? { label } : option);
  };

  if (selectedValue) add({ label: selectedValue });
  options.forEach(add);
  return merged;
};

const isOptionEqualToValue = (option, value) =>
  getOptionLabel(option) === getOptionLabel(value);

export default function TreatmentForm({
  data,
  handleClose,
  familyMemberId,
  isProband,
  isEditing,
  model,
  setUnsavedChanges
}) {
  const { content } = useContext(AppContext);
  const { pedigreeSelectedUsers: users } = content;
  const { selectedUser: user } = users;
  const { setSnack } = useContext(SnackContext);

  const clinicalSuggestions = useClinicalSuggestions({
    familyMemberId,
    isProband
  });
  const { analyzingNote, analyzeText, acceptSuggestions } = clinicalSuggestions;

  const patientDob = data?.dob;
  // The age on the calendar day of the treatment date (calculateAgeOnDate). The
  // date is handed over as stored — a picker instant, or UTC midnight from the
  // spreadsheet importer and demo seed — and only the raw value says which, so
  // it is read by toLocalDateFromPickerOrImport. `calculateAge` counted elapsed
  // time from a UTC-midnight birth date: a year short on the birthday at any
  // positive offset, and a positive age for a date before the birth, which is
  // now blank. The date field shows the day it read (asPickerDay), so the day
  // on screen is the day the age is from.
  const asPickerDay = toLocalDateFromPickerOrImport;
  const computeAgeAtRecord = React.useCallback(
    (dateValue) => calculateAgeOnDate(patientDob, dateValue),
    [patientDob]
  );

  const defaultValues = isEditing
    ? { ...model }
    : {
        familyId: user.familyId,
        familyMemberId,
        treatment: '',
        treatmentDate: null,
        treatmentEndDate: null,
        comment: '',
        ageAtRecord: ''
      };

  const [formValues, setFormValues] = useState(defaultValues);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [treatmentOptions, setTreatmentOptions] = useState([]);
  const [treatmentSearch, setTreatmentSearch] = useState('');
  const [treatmentLoading, setTreatmentLoading] = useState(false);
  // Why the ICD-10-CM lookup did not run — switched off in Settings, or the
  // terminology service unreachable — so the field never reads "No matches"
  // when nothing was searched (TGCLOUD-1845).
  const [treatmentUnavailable, setTreatmentUnavailable] = useState(null);

  const treatmentOptionList = useMemo(
    () => mergeOptions(treatmentOptions, formValues.treatment),
    [treatmentOptions, formValues.treatment]
  );

  useEffect(() => {
    const query = treatmentSearch.trim();
    if (query.length < TREATMENT_SEARCH_MIN_QUERY_LENGTH) {
      setTreatmentOptions([]);
      setTreatmentLoading(false);
      setTreatmentUnavailable(null);
      return;
    }

    let active = true;
    const timeout = setTimeout(() => {
      setTreatmentLoading(true);
      axios
        .get(APICONSTANTS.treatmentSearch, {
          params: {
            q: query
          }
        })
        .then((res) => {
          if (!active) return;
          setTreatmentUnavailable(readTerminologyAvailability(res.data));
          setTreatmentOptions(
            Array.isArray(res.data?.results) ? res.data.results : []
          );
        })
        .catch(() => {
          if (!active) return;
          setTreatmentOptions([]);
          setTreatmentUnavailable(terminologyAvailabilityFromError());
        })
        .finally(() => {
          if (active) setTreatmentLoading(false);
        });
    }, TREATMENT_SEARCH_DEBOUNCE_MS);

    return () => {
      active = false;
      clearTimeout(timeout);
    };
  }, [treatmentSearch]);

  React.useEffect(() => {
    if (!patientDob) return;
    const nextAge = computeAgeAtRecord(formValues.treatmentDate);
    setFormValues((prev) => {
      const currentAge = prev.ageAtRecord === undefined ? '' : prev.ageAtRecord;
      if (nextAge === '' && currentAge === '') return prev;
      if (nextAge === currentAge) return prev;
      return { ...prev, ageAtRecord: nextAge };
    });
  }, [patientDob, computeAgeAtRecord, formValues.treatmentDate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'treatmentDate') {
      setFormValues((prev) => ({
        ...prev,
        ageAtRecord: computeAgeAtRecord(value)
      }));
    }
    setFormValues((prev) => ({
      ...prev,
      [name]: value
    }));
    setUnsavedChanges((prev) => ({ ...prev, edit: true }));
  };

  const deleteNote = async () => {
    if (!model?._id) return;
    setLoading(true);
    axios
      .delete(`/clinical-notes/note`, {
        params: {
          _id: model._id
        }
      })
      .then(() => {
        setSnack({
          status: true,
          msg: 'Record deleted',
          severity: 'success'
        });
        handleClose({ exit: true });
      })
      .catch((err) => {
        setSnack({
          status: true,
          msg: `Delete failed! ${err}`,
          severity: 'error'
        });
      })
      .finally(() => {
        setLoading(false);
        setOpen(false);
      });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();

    return axios
      .put(APICONSTANTS.treatment, { ...formValues })
      .then((res) => {
        handleClose({ exit: true });
        if (res.status === 200) {
          setSnack({
            status: true,
            msg: 'Successfully saved',
            severity: 'success'
          });
        } else {
          setSnack({
            status: true,
            msg: `Upload failed!`,
            severity: 'error'
          });
        }
      })
      .catch((err) => {
        setSnack({
          status: true,
          msg: `Upload failed! ${err}`,
          severity: 'error'
        });
      });
  };

  return (
    <form className="user-form">
      <ClinicalNoteDeleteModal
        loading={loading}
        open={open}
        handleCloseModal={() => setOpen(false)}
        onConfirm={deleteNote}
        title="Are you sure you want to delete this Treatment record?"
        content="Warning: This action will permanently delete this Treatment record."
      />
      <Grid container spacing={2}>
        <Grid id="modaltop" size={6}>
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Treatment</FormLabel>
          </Grid>
          <Box sx={{ position: 'relative' }}>
            <TextInput
              label=""
              variant="outlined"
              fullWidth
              type="autocomplete"
              name="treatment"
              value={formValues.treatment}
              onChange={(val) =>
                handleChange({
                  target: { name: 'treatment', value: val?.label || val || '' }
                })
              }
              onInputChange={(event, newValue, reason) => {
                if (!reason || reason === 'input' || reason === 'clear') {
                  setTreatmentSearch(newValue || '');
                }
              }}
              options={treatmentOptionList}
              loading={treatmentLoading}
              loadingText="Searching..."
              noOptionsText={
                treatmentUnavailable
                  ? 'Lookup unavailable — this is not a result'
                  : treatmentSearch.trim().length >=
                    TREATMENT_SEARCH_MIN_QUERY_LENGTH
                  ? 'No matches'
                  : 'Type to search'
              }
              isOptionEqualToValue={isOptionEqualToValue}
              // It searches ICD-10-CM, and saves the text only — it never did
              // search SNOMED (TGCLOUD-1846).
              placeholder="Enter treatment or search ICD-10-CM"
            />
            {treatmentUnavailable && (
              <FormHelperText
                role="status"
                sx={{ mx: 0, color: 'warning.dark' }}
              >
                {treatmentUnavailable.message}
              </FormHelperText>
            )}
          </Box>
        </Grid>

        <Grid size={6}>
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Treatment Date</FormLabel>
          </Grid>
          <DateInput
            name="treatmentDate"
            value={asPickerDay(formValues.treatmentDate)}
            onChange={(val) =>
              handleChange({
                target: { name: 'treatmentDate', value: val, type: 'date' }
              })
            }
            variant="outline"
          />
        </Grid>

        <Grid size={6}>
          <TextInput
            label="Age at Treatment"
            name="ageAtRecord"
            type="number"
            value={formValues.ageAtRecord ?? ''}
            inputProps={{ min: 0, className: 'no-spinners' }}
            // Disabled (muted) only when an age is actually being
            // auto-calculated, i.e. DoB AND treatment date both set.
            // Editable when either is missing so a known age can be
            // recorded directly.
            disabled={!!patientDob && !!formValues.treatmentDate}
            onChange={
              patientDob && formValues.treatmentDate
                ? undefined
                : (e) => {
                    const val = e.target.value;
                    const parsed = val === '' ? '' : Number(val);
                    if (val === '' || !Number.isNaN(parsed)) {
                      setFormValues((prev) => ({
                        ...prev,
                        ageAtRecord: val === '' ? '' : parsed
                      }));
                      setUnsavedChanges((prev) => ({ ...prev, edit: true }));
                    }
                  }
            }
          />
        </Grid>

        <Grid size={6}>
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Treatment End Date</FormLabel>
          </Grid>
          <DateInput
            name="treatmentEndDate"
            value={formValues.treatmentEndDate}
            onChange={(val) =>
              handleChange({
                target: { name: 'treatmentEndDate', value: val, type: 'date' }
              })
            }
            variant="outline"
          />
        </Grid>

        <Grid size={12}>
          <Grid sx={{ paddingBottom: 1 }}>
            <FormLabel>Comment</FormLabel>
          </Grid>
          <TextInput
            label=""
            variant="outlined"
            fullWidth
            multiline
            name="comment"
            value={formValues.comment}
            onChange={handleChange}
            placeholder="Add comments"
            InputProps={{
              endAdornment: formValues.comment ? (
                <InputAdornment position="end" sx={{ alignSelf: 'center' }}>
                  <Tooltip title="Analyse for clinical terms">
                    <span style={{ lineHeight: 0 }}>
                      <IconButton
                        size="medium"
                        onClick={() =>
                          analyzeText(formValues.comment, 'manual')
                        }
                        disabled={analyzingNote}
                      >
                        <AutoAwesomeIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </InputAdornment>
              ) : null
            }}
          />
        </Grid>

        <ClinicalSuggestionsPanel
          state={clinicalSuggestions}
          onAccept={() =>
            acceptSuggestions().then((result) => {
              if (result?.success) {
                const { count = {} } = result;
                const parts = [];
                if (count.phenotypes)
                  parts.push(`${count.phenotypes} phenotype(s)`);
                if (count.genotypes)
                  parts.push(`${count.genotypes} genotype(s)`);
                if (count.diseases) parts.push(`${count.diseases} disease(s)`);
                setSnack({
                  status: true,
                  msg: `Added ${parts.join(', ')} to the record`,
                  severity: 'success'
                });
              } else {
                setSnack({
                  status: true,
                  msg: 'Failed to save suggestions',
                  severity: 'error'
                });
              }
            })
          }
        />

        <ModalButtons
          handleClose={handleClose}
          onClick={handleSubmit}
          isEditing={isEditing}
          addLabel="Save Treatment"
          hideContinueAdding
          saveButtonProps={{ sx: { whiteSpace: 'nowrap' } }}
          intermediateBtnLabel="Delete"
          intermediateAction={isEditing ? () => setOpen(true) : null}
        />
      </Grid>
    </form>
  );
}
